import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import RoomStatus from '../components/RoomStatus.jsx';
import { startDemoTimer, stopDemoTimer } from '../services/RoomStatusService.js';
import { useApiContext } from "../context/ApiContext.jsx";
import { useGsapConfetti } from "../hooks/useGsapConfetti.jsx";
import { useSfx } from "../hooks/useSfx.jsx";
import { AuthContext } from "../context/AuthContext.jsx";
import { useRoom } from '../context/RoomContext.jsx';
import UserItem from './UserItem.jsx';
import Performance from './Performance.jsx';
import Tournament from './Tournament.jsx';
import { computeTotalScoreFromQuestions } from '../helpers/ScoreUtils.js';
import { DemoQuestions } from '../helpers/DemoQuestions.js';


// Calcolo analisi DEMO lato client a partire da "questions"
const buildDemoAnalysis = (questions = [], sessionInfo, elapsed) => {
	if (!questions.length) return null;

	const items = questions.map((q) => {
		const correctChoices = Array.isArray(q.correctChoices) ? q.correctChoices : [];
		const correctIds = correctChoices.map(c => String(c.id));

		const inputs = Array.isArray(q.inputs) ? q.inputs : [];
		const selectedIds = inputs.map(i => i.choiceId ? String(i.choiceId) : null).filter(Boolean);

		const maxScore = correctIds.length;

		const matchedCorrectCount = selectedIds.filter(id => correctIds.includes(id)).length;
		const wrongSelectedCount = selectedIds.length - matchedCorrectCount;

		const percSuccess = selectedIds.length > 0
			? Math.round((matchedCorrectCount * 100) / selectedIds.length)
			: 0;

		let score = 0;

		if (wrongSelectedCount > 0) {
			score = 0;
		} else if (maxScore === 0 || selectedIds.length === 0) {
			score = 0;
		} else {
			score = Math.round((matchedCorrectCount / maxScore) * 100) / 100;
			if (!Number.isFinite(score)) score = 0;
		}

		return {
			questionId: q.id,
			correctChoiceIds: correctIds,
			selectedChoiceIds: selectedIds,
			matchedCorrectCount,
			wrongSelectedCount,
			percSuccess,
			score
		};
	});

	const totalScore = Math.round(items.reduce((sum, it) => sum + (Number(it.score) || 0), 0) * 100) / 100;

	const successAnswers = items.reduce((sum, it) => sum + (it.matchedCorrectCount || 0), 0);
	const errorAnswers = items.reduce((sum, it) => sum + (it.wrongSelectedCount || 0), 0);

	return {
		nickName: sessionInfo?.nickName ?? 'Player',
		avatarId: sessionInfo?.currentAvatarId ?? null,
		totalScore,
		successAnswers,
		errorAnswers,
		totalSeconds: elapsed,
		position: 1,
		items,
	};
};

const ResultsPage = ({ questions }) => {
	const { api, isDemo } = useApiContext();
	const { sessionInfo, setSessionInfo } = useContext(AuthContext);
	const { roomState } = useRoom();
	const [activeTab, setActiveTab] = useState(null)
	const [analysis, setAnalysis] = useState(null);
	const { emitterRef, fireConfetti } = useGsapConfetti();
	const sfx = useSfx();
	const hasPlayedTadaRef = useRef(false);

	const demoElapsedRef = useRef(null);

	// DEMO: aggiorna la posizione quando cambia la classifica
	useEffect(() => {
		if (!sessionInfo || !isDemo) return;
		if (!questions || !questions.length) return;

		if (demoElapsedRef.current == null) {
			demoElapsedRef.current = stopDemoTimer();
		}

		const demoAnalysis = buildDemoAnalysis(questions, sessionInfo, demoElapsedRef.current);
		demoAnalysis.position = roomState.currentUserPosition;
		setAnalysis(demoAnalysis);
	}, [sessionInfo, isDemo, questions, roomState?.currentUserPosition]);

	// REAL MODE: richiama session-results 
	useEffect(() => {
		if (!sessionInfo || isDemo) return;
		//if (!roomState?.roomItemsOrder) return;

		api
			.get('/api/trivia/session-results')
			//.get('/react-app/json/session-results.json')
			.then(res => {
				const data = res.data;
				setAnalysis(data);
			});
	}, [sessionInfo, isDemo, api, questions, roomState?.roomItemsOrder]);
	//[sessionInfo, isDemo, api, questions, roomState?.currentUserPosition]);

	useEffect(() => {
		if (!activeTab) return;
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, [activeTab])

	useEffect(() => {
		if (!analysis) return;
		setActiveTab(prev => prev === null ? (sessionInfo.sessionType === 1 ? 1 : 0) : prev);
		if (sessionInfo.sessionType === 1 && !hasPlayedTadaRef.current) {
			hasPlayedTadaRef.current = true;
			setTimeout(() => {
				fireConfetti();
			}, 500)
			sfx.playTada();
		}
		
	}, [analysis])

	const handleUserClick = (ev, sessionId) => {
		ev.preventDefault();
		console.log('User click:', sessionId);

		if (isDemo) {
			// DEMO MODE:
			// non richiamiamo nulla, usiamo l'analysis già mockato
			setActiveTab(1);
			return;
		}

		// REAL MODE:
		api.get('/api/trivia/session-results', {
			params: { sessionId: sessionId },
		}).then(res => {
			setAnalysis(res.data);
			setActiveTab(1);
		})
	};

	return (
		<>
			<div
				id="emitter"
				ref={emitterRef}
				style={{
					position: "fixed",
					top: "300px",
					left: "40%",
					width: "20px",
					height: "20px"
				}}
			/>
			<div id="results-page" className={`${sessionInfo.typeCode == "TournamentSemifinals" ? "tournament-results-page" : ""} ${sessionInfo.sessionType > 1 ? "admin-results-page" : ""} mb-5 game-content container-xl animate__animated animate__fadeIn animate__delay-1s`}>
				<div className="game-header d-flex flex-wrap flex-sm-nowrap justify-content-sm-between align-items-center text-center text-sm-start">
					<div className="d-flex align-items-center">
						<i className="bi bi-trophy" style={{ fontSize: "4.2rem" }}></i>
						<h1 className="ms-3 fw-bold lh-1 fs-3 fs-sm-3 mb-0 text-start">
							{sessionInfo.roomName}
							<small className="d-block fw-semibold fs-4 mt-1" style={{ color: "#b0b2ab" }}>{sessionInfo.sessionType > 1 ? "L'istantanea della prova" : "I risultati della prova"}</small>
						</h1>
					</div>
					<div className="mascotte-shadowed-container">
						{sessionInfo.template?.mascotNormalMediaId ?
							<img className="ms-3 mb-0 d-none d-sm-block mascotte mascotte-shadowed" src={`/api/trivia/media/${sessionInfo.template.mascotNormalMediaId}`} style={{ width: 65 }} />
						: ""}
						
					</div>
				</div>
				{activeTab != null && (
					<div className="game-subheader d-flex flex-wrap flex-lg-nowrap justify-content-between align-items-center">

						<a href="#" onClick={(ev) => { ev.preventDefault(); setActiveTab(0) }} className={activeTab == 0 ? "active" : ""}>
							La classifica
						</a>

						<a href="#"
							onClick={(ev) => { ev.preventDefault(); setActiveTab(1) }}
							className={`${activeTab == 1 ? "active" : ""}`}
							style={{ pointerEvents: (sessionInfo.sessionType > 1 ? "none" : "unset") }}>
							{sessionInfo.sessionType == 1 ? "La tua performance" : "La performance"}
						</a>

						{sessionInfo.typeCode == "TournamentSemifinals" ? (
							<a href="#" onClick={(ev) => { ev.preventDefault(); setActiveTab(2) }} className={activeTab == 2 ? "active" : ""}>
								Il torneo
							</a>
						) : ""}

					</div>
				)}

				{activeTab != null && (
					<div className="px-md-4">
						<div className={`animate__animated animate__fadeIn px-2 px-sm-3 px-lg-5 w-100 mt-5 mt-sm-7 ${activeTab != 0 ? "d-none" : ""}`} style={{ animationdDuration: "0.3s" }}>
							<RoomStatus
								handleUserClick={handleUserClick}
								isFinalStep={1}
								questions={questions}
							></RoomStatus>
						</div> 

						{analysis && (
							<Performance analysis={analysis} activeTab={activeTab} questions={questions} />
						)}

						{sessionInfo.typeCode == "TournamentSemifinals" ? (
							<Tournament activeTab={activeTab} />
						) : ""}

					</div>
				)}

			</div>
		</>
	);
};

export default ResultsPage;
